import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { CaseStatusBadge } from '../components/CaseStatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Briefcase, FileText, AlertCircle, Search } from 'lucide-react';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LawyerDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, casesRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/cases`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setCases(casesRes.data);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = cases.filter(c =>
    c.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div data-testid="lawyer-dashboard">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-heading">{t('dashboard')}</h1>
          <p className="text-[#475569] mt-1">{t('welcome_back')}, {user?.name}</p>
        </div>

        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-lg bg-[#F3E8FF] flex items-center justify-center mb-3">
                  <Briefcase className="w-5 h-5 text-[#7E22CE]" />
                </div>
                <div className="text-2xl font-bold text-[#0F172A] font-heading">{stats.my_cases}</div>
                <div className="text-sm text-[#475569] mt-1">{t('my_cases')}</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-lg bg-[#D1FAE5] flex items-center justify-center mb-3">
                  <Briefcase className="w-5 h-5 text-[#059669]" />
                </div>
                <div className="text-2xl font-bold text-[#0F172A] font-heading">{stats.active_cases}</div>
                <div className="text-sm text-[#475569] mt-1">{t('active_cases')}</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-lg bg-[#FEE2E2] flex items-center justify-center mb-3">
                  <AlertCircle className="w-5 h-5 text-[#DC2626]" />
                </div>
                <div className="text-2xl font-bold text-[#0F172A] font-heading">{stats.pending_documents}</div>
                <div className="text-sm text-[#475569] mt-1">{t('pending_documents')}</div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="mb-4">
          <div className="relative max-w-sm">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input placeholder={t('search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="ps-9 h-10" data-testid="lawyer-cases-search" />
          </div>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-[#0F172A] font-heading">{t('my_cases')}</CardTitle>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow className="border-b border-slate-100">
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('case_title')}</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('case_type')}</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('case_status')}</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('client')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(c => (
                <TableRow
                  key={c.case_id}
                  className="border-b border-slate-100 cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/lawyer/cases/${c.case_id}`)}
                  data-testid={`lawyer-case-row-${c.case_id}`}
                >
                  <TableCell className="font-medium text-[#0F172A]">{c.title}</TableCell>
                  <TableCell className="text-[#475569] capitalize">{c.case_type}</TableCell>
                  <TableCell><CaseStatusBadge status={c.status} /></TableCell>
                  <TableCell className="text-[#475569]">{c.client_id?.slice(0, 12)}</TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center py-12 text-[#94A3B8]">{t('no_cases')}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </DashboardLayout>
  );
}
